import { useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import Landing from './pages/Landing.jsx';
import Pricing from './pages/Pricing.jsx';
import { RouteSeo } from './components/SeoHead.jsx';
import Dashboard from './pages/Dashboard.jsx';
import PostDetail from './pages/PostDetail.jsx';
import ProjectDetail from './pages/ProjectDetail.jsx';
import Projects from './pages/Projects.jsx';
import NewProject from './pages/NewProject.jsx';
import Posts from './pages/Posts.jsx';
import Inbox from './pages/Inbox.jsx';
import Analysis from './pages/Analysis.jsx';
import Roadmap from './pages/Roadmap.jsx';
import Login from './pages/Login.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import Signup from './pages/Signup.jsx';
import VerifyEmail from './pages/VerifyEmail.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import SearchBar from './components/SearchBar.jsx';
import MobileNav from './components/MobileNav.jsx';
import { NAV_ITEMS } from './lib/navItems.js';
import UserMenu from './components/UserMenu.jsx';
import OrgSwitcher from './components/OrgSwitcher.jsx';
import { ProjectScopeSwitcher } from './components/ProjectScopeSwitcher.jsx';
import QuotaBanner from './components/QuotaBanner.jsx';
import SocialErrorBanner from './components/SocialErrorBanner.jsx';
import AiChatDrawer from './components/AiChatDrawer.jsx';

// Legal pages — public, required for Meta App Review + GDPR
import Privacy from './pages/legal/Privacy.jsx';
import Terms from './pages/legal/Terms.jsx';
import DataDeletion from './pages/legal/DataDeletion.jsx';

// OAuth callbacks — public because the user is mid-OAuth, may have lost
// session. Identity re-established via single-use state token.
import InstagramCallback from './pages/oauth/InstagramCallback.jsx';

// Account-area pages (each rendered inside AccountLayout's sidebar shell)
import AccountLayout from './layouts/AccountLayout.jsx';
import Profile from './pages/account/Profile.jsx';
import Password from './pages/account/Password.jsx';
import Appearance from './pages/account/Appearance.jsx';
import Organization from './pages/account/Organization.jsx';
import TeamWrapper from './pages/account/TeamWrapper.jsx';
import AuditLog from './pages/account/AuditLog.jsx';
import Billing from './pages/account/Billing.jsx';
import Support from './pages/account/Support.jsx';
import AdminQuotas from './pages/admin/Quotas.jsx';
import AdminOverview from './pages/admin/Overview.jsx';

/**
 * Top-level routing.
 *
 *   /                       public landing
 *   /login                  login form
 *   /dashboard              recent posts + projects
 *   /projects/:key          project + loop status + pillars + connections
 *   /posts/:id              post + assets + audit
 *
 *   /account                Profile (sidebar layout, default)
 *   /account/password       Change password
 *   /account/appearance     Theme picker (dark-only today)
 *   /account/organization   Org info + stats
 *   /account/team           Team management
 *   /account/audit          Audit log (owner/admin only)
 *
 *   /team                   → /account/team (legacy redirect)
 */
export default function App() {
  return (
    <>
      {/* Global — surfaces ?social_error=... on any route. The user lands
          here from a failed OAuth round-trip; without this banner the
          error sat invisibly in the URL bar. */}
      <SocialErrorBanner />
      {/* Per-route SEO head tags for public marketing pages — looks up
          location.pathname in src/content/seoMeta.js. No-op on app
          routes not in the registry. */}
      <RouteSeo />
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* Public legal pages — required by Meta App Review + GDPR. NOT
          behind ProtectedRoute (Meta crawls them anonymously). */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/data-deletion" element={<DataDeletion />} />

      {/* OAuth callbacks — public for the same reason the legacy backend
          callback is unauthenticated: user is mid-OAuth, may have lost
          session. State token re-establishes identity inside the page. */}
      <Route path="/oauth/instagram/callback" element={<InstagramCallback />} />

      {/* Main app */}
      <Route path="/dashboard" element={<Protected><AppShell><Dashboard /></AppShell></Protected>} />
      <Route path="/projects" element={<Protected><AppShell><Projects /></AppShell></Protected>} />
      <Route path="/projects/new" element={<Protected><AppShell><NewProject /></AppShell></Protected>} />
      <Route path="/projects/:key" element={<Protected><AppShell><ProjectDetail /></AppShell></Protected>} />
      <Route path="/projects/:key/analysis" element={<Protected><AppShell><Analysis /></AppShell></Protected>} />
      <Route path="/posts" element={<Protected><AppShell><Posts /></AppShell></Protected>} />
      <Route path="/posts/:id" element={<Protected><AppShell><PostDetail /></AppShell></Protected>} />
      <Route path="/inbox" element={<Protected><AppShell><Inbox /></AppShell></Protected>} />

      {/* Account area — sidebar layout */}
      <Route path="/account"              element={<Protected><AppShell><AccountLayout><Profile /></AccountLayout></AppShell></Protected>} />
      <Route path="/account/password"     element={<Protected><AppShell><AccountLayout><Password /></AccountLayout></AppShell></Protected>} />
      <Route path="/account/appearance"   element={<Protected><AppShell><AccountLayout><Appearance /></AccountLayout></AppShell></Protected>} />
      <Route path="/account/organization" element={<Protected><AppShell><AccountLayout><Organization /></AccountLayout></AppShell></Protected>} />
      <Route path="/account/team"         element={<Protected><AppShell><AccountLayout><TeamWrapper /></AccountLayout></AppShell></Protected>} />
      <Route path="/account/audit"        element={<Protected><AppShell><AccountLayout><AuditLog /></AccountLayout></AppShell></Protected>} />
      <Route path="/account/billing"      element={<Protected><AppShell><AccountLayout><Billing /></AccountLayout></AppShell></Protected>} />
      <Route path="/account/support"      element={<Protected><AppShell><AccountLayout><Support /></AccountLayout></AppShell></Protected>} />
      <Route path="/account/roadmap"      element={<Protected><AppShell><AccountLayout><Roadmap /></AccountLayout></AppShell></Protected>} />

      {/* Platform-operator dashboards (users.is_platform_admin; server
          enforces via requirePlatformAdmin — the redirect here is UX only) */}
      <Route path="/admin" element={<Protected><AppShell><AdminOverview /></AppShell></Protected>} />
      <Route path="/admin/quotas" element={<Protected><AppShell><AdminQuotas /></AppShell></Protected>} />

      {/* Legacy redirect — /team used to be top-level before the sidebar */}
      <Route path="/team" element={<Navigate to="/account/team" replace />} />

      <Route path="*" element={<Protected><AppShell><NotFound /></AppShell></Protected>} />
    </Routes>
    </>
  );
}

// Shorthand to avoid repeating <ProtectedRoute> markup on every line above.
function Protected({ children }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function AppShell({ children }) {
  const location = useLocation();
  // Owned here rather than inside SearchBar so the mobile drawer can open the
  // search modal — its trigger button is hidden below 768px.
  const [searchOpen, setSearchOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="app">
      <header className="topbar">
        <MobileNav open={navOpen} onOpenChange={setNavOpen} onOpenSearch={() => setSearchOpen(true)} />
        <Link to="/dashboard" className="brand">
          <img src="/icons/markaii-m.png?v=2" alt="" className="brand-icon" />
          <span className="brand-label">Markaii</span>
        </Link>
        <SearchBar open={searchOpen} onOpenChange={setSearchOpen} />
        <nav className="topnav">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={item.isActive(location.pathname) ? 'active' : ''}
              aria-label={item.label}
            >
              <span className="topnav-icon" aria-hidden="true">{item.icon}</span>
              <span className="topnav-label">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="topbar-right">
          <ProjectScopeSwitcher />
          <OrgSwitcher />
          <UserMenu />
        </div>
      </header>

      <QuotaBanner />
      <main className="main">{children}</main>

      <footer className="footer">
        <span>markaii.com — multi-brand AI marketing</span>
      </footer>

      {/* In-app AI assistant — floating ✨, on every authenticated page. */}
      <AiChatDrawer />
    </div>
  );
}

function NotFound() {
  return (
    <div className="empty">
      <h2>Page not found</h2>
      <p>That route doesn’t exist (yet).</p>
      <Link to="/dashboard" className="btn">Back to dashboard</Link>
    </div>
  );
}
